import { formatDateTime } from '@/utils/datetime';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/doc-ai`;

const Row = ({ label, children, testId }) => (
  <div className="flex items-baseline gap-2">
    <span className="w-28 shrink-0 text-xs font-medium text-zinc-500">{label}</span>
    <span className="text-sm text-zinc-800 break-all" data-testid={testId}>{children}</span>
  </div>
);

const Chip = ({ tone, children, testId }) => {
  const cls = {
    ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    bad: 'bg-rose-50 text-rose-700 border-rose-200',
    neutral: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  }[tone || 'neutral'];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
      data-testid={testId}>{children}</span>
  );
};

const USED_BY = ['1件ずつのアップロード', '複数ファイルのアップロード', 'フォルダーへのドロップ',
  '一括読み取り', '技術図面', '仕様書', 'マニュアル', '試験報告書', '新版の再読み取り',
  'もう一度読み取る', '図面としてもう一度読み取る', 'AIタグ・メタデータの再生成'];

/** AI設定「書類・文字読み取り」— 書類の読み取り・分類（document_metadata）の状態と検証。 */
export const DocReadCard = () => {
  const [data, setData] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [showEffective, setShowEffective] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/config`);
      setData(r.data);
    } catch { /* 権限のない画面では静かに何も出さない */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/test`, {});
      setResult(r.data);
      r.data.ok ? toast.success('読み取り設定のテストに成功しました')
        : toast.error('読み取り設定のテストで問題が見つかりました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'テストに失敗しました');
    } finally { setTesting(false); }
  };

  if (!data) return null;
  const e = data.effective || {};
  const lt = data.last_test;
  const status = e.capability_error ? 'bad' : e.model ? 'ok' : 'warn';
  const statusLabel = e.capability_error ? '設定不足' : e.model ? '利用可能' : '設定不足';

  return (
    <div className="rounded-lg border border-teal-200 bg-white shadow-sm overflow-hidden"
      data-testid="doc-read-card">
      <div className="border-b border-zinc-200 bg-teal-50/60 px-5 py-3">
        <p className="text-xs font-semibold text-teal-800">書類・文字読み取り</p>
        <h3 className="text-base font-semibold text-zinc-900">書類の読み取り・分類</h3>
        <p className="mt-0.5 text-xs text-zinc-600">
          アップロードしたPDF・画像・技術図面を読み取り、検索用の情報と分類候補を作成します。
        </p>
      </div>

      {e.capability_error && (
        <p className="mx-5 mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          data-testid="doc-read-capability-warning">{e.capability_error}</p>
      )}
      {e.fallback_error && (
        <p className="mx-5 mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          data-testid="doc-read-fallback-warning">{e.fallback_error}</p>
      )}

      <div className="grid gap-2.5 px-5 py-4 sm:grid-cols-2">
        <Row label="使用モデル" testId="doc-read-model">{e.model || '未設定'}</Row>
        <Row label="プロバイダー" testId="doc-read-provider">{e.provider_label}</Row>
        <Row label="フォールバック" testId="doc-read-fallback">{e.fallback_model || '未設定'}</Row>
        <Row label="画像入力" testId="doc-read-vision">
          <Chip tone={e.vision ? 'ok' : e.ocr_engine_vision ? 'warn' : 'bad'}>
            {e.vision ? '対応' : e.ocr_engine_vision ? '文字抽出エンジン経由' : '非対応'}
          </Chip>
        </Row>
        <Row label="図面専用読み取り" testId="doc-read-drawing-mode">
          <Chip tone={e.engineering_drawing_enabled ? 'ok' : 'warn'}>
            {e.engineering_drawing_enabled ? '有効' : '無効'}
          </Chip>
        </Row>
        <Row label="状態" testId="doc-read-status">
          <Chip tone={status}>{statusLabel}</Chip>
        </Row>
        <Row label="最終テスト" testId="doc-read-last-test">
          {lt?.at ? `${formatDateTime(lt.at)}（${lt.ok ? '成功' : '失敗'}）` : '未実施'}
        </Row>
        <Row label="読み取り方法" testId="doc-read-modes">通常の書類 / 技術図面 / タイトル欄優先 / 画像・写真</Row>
      </div>

      <div className="border-t border-zinc-100 px-5 py-3">
        <p className="text-xs font-medium text-zinc-500 mb-1.5">処理構成</p>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-600"
          data-testid="doc-read-pipeline">
          {['文字抽出エンジン', '書類読み取りAI', '検証', '検索登録'].map((s, i) => (
            <span key={s} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-zinc-300">→</span>}
              <span className="rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5">{s}</span>
            </span>
          ))}
        </p>
        <p className="mt-1.5 text-xs text-zinc-400">
          文字抽出エンジンは画像上の文字を抽出し、書類読み取りAIがその文字とレイアウトを理解して書類情報を提案します。
        </p>
      </div>

      <div className="border-t border-zinc-100 px-5 py-3">
        <p className="text-xs font-medium text-zinc-500 mb-1.5">このモデルが使われる操作</p>
        <p className="flex flex-wrap gap-1.5" data-testid="doc-read-used-by">
          {USED_BY.map((u) => (
            <span key={u} className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-600">{u}</span>
          ))}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-zinc-100 px-5 py-3">
        <button onClick={() => setShowEffective((v) => !v)}
          className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 transition-colors duration-200 hover:bg-zinc-50"
          data-testid="doc-read-effective-btn">適用設定を確認</button>
        <button onClick={runTest} disabled={testing}
          className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 transition-colors duration-200 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="doc-read-test-btn">{testing ? 'テスト中…' : '設定をテスト'}</button>
        <span className="ml-auto text-xs text-zinc-400" data-testid="doc-read-route-key">
          Route: {e.route_key}　Prompt/Profile: {e.prompt_version}
        </span>
      </div>

      {showEffective && (
        <div className="grid gap-2 border-t border-zinc-100 bg-zinc-50 px-5 py-4 sm:grid-cols-2"
          data-testid="doc-read-effective">
          <p className="text-xs font-medium text-teal-800 sm:col-span-2">適用設定（技術図面として読み取る場合）</p>
          <Row label="機能ルート（図面モードで解決）">{e.route_key}</Row>
          <Row label="プロバイダー">{e.provider_label}</Row>
          <Row label="使用モデル">{e.model}</Row>
          <Row label="フォールバック">{e.fallback_model || '未設定'}</Row>
          <Row label="入力方式">{e.input_strategy}</Row>
          <Row label="読み取り方法">{e.processing_mode_label}</Row>
          <Row label="抽出プロファイル">{e.extraction_profile}</Row>
          <Row label="プロンプト版">{e.prompt_version}</Row>
          <Row label="スキーマ版">{e.schema_version}</Row>
          <Row label="タイムアウト">{e.timeout_seconds} 秒</Row>
          <Row label="入力ページ上限">{e.max_input_pages} ページ</Row>
          <Row label="同時実行数">{e.concurrency}</Row>
          <Row label="文字抽出エンジン">{e.ocr_engine_model}</Row>
        </div>
      )}

      {result && (
        <div className="border-t border-zinc-100 px-5 py-4" data-testid="doc-read-test-result">
          <p className="mb-2 text-xs font-medium text-zinc-500">テスト結果</p>
          <div className="space-y-1.5">
            {(result.steps || []).map((s) => (
              <div key={s.key} className="flex items-start gap-2 text-sm"
                data-testid={`doc-read-test-step-${s.key}`}>
                <Chip tone={s.ok ? 'ok' : 'bad'}>{s.ok ? '成功' : '失敗'}</Chip>
                <span className="text-zinc-700">{s.label}</span>
                <span className="text-xs text-zinc-400">{s.detail}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-zinc-500">
            使用モデル: {result.route?.model}　フォールバック使用: {result.route?.fallback_model ? '設定あり' : 'なし'}
            {result.elapsed_ms ? `　所要時間: ${result.elapsed_ms} ms` : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default DocReadCard;
